import * as React from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Badge, Button, IconButton, type IconComponentType } from '@restworld/ui-ds-base';

import { SkeletonTextLoading } from '@restworld/ui-components';

import { cn } from '@restworld/utils-common';
export interface SlotPurchaseCardProps {
  isActive?: boolean;
  slotNumber?: number;
  onSlotAdd?: () => void;
  onSlotMinus?: () => void;
  headerTitle?: string;
  bundlePrice?: number;
  slotPrice?: number;
  type: 'custom' | 'bundle' | 'subscription' | 'trial';
  discountPrice?: number;
  discount?: { coupon: string; discount: number };
  isMostRequested?: boolean;
  onClick?: () => void;
  slotPurchaseDescriptions?: string[];
  loading?: boolean;
  description?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  ctaStartIcon?: IconComponentType;
  isCtaDisabled?: boolean;
  isCtaLoading?: boolean;
}

const getCardContainerStyles = (isActive: boolean, type: string) => {
  const base =
    'w-full max-w-[720px] p-5 min-h-[320px] cursor-pointer relative overflow-hidden bg-neutral-10 rounded-[16px] flex flex-col justify-between items-start gap-4 transition-all duration-200';
  const border = isActive ? 'border-2 border-purple-500' : 'border-2 border-neutral-90';
  const trial = type === 'trial' ? 'bg-purple-50' : '';
  const shadow = isActive
    ? type === 'trial'
      ? '[box-shadow:0_10px_28px_0_rgba(72,36,133,0.55),0_0_0_3px_rgba(102,51,187,0.10)]'
      : '[box-shadow:0_10px_28px_0_rgba(0,0,26,0.20),0_0_0_3px_rgba(102,51,187,0.10)]'
    : type === 'trial'
    ? '[box-shadow:0_4px_16px_0_rgba(72,36,133,0.35)]'
    : '[box-shadow:0_4px_16px_0_rgba(0,0,26,0.12)]';
  const hover = !isActive ? 'hover:border-[#693eb5] hover:[box-shadow:0_4px_16px_0_rgba(0,0,26,0.15)]' : '';
  return cn(base, border, trial, shadow, hover);
};

const CardContainer = ({ isActive, type, className, ...props }: any) => (
  <div className={cn(getCardContainerStyles(isActive, type), className)} {...props} />
);

const ContentContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex flex-col justify-start items-start gap-8', className)} {...props} />
);
const HeaderTop = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch justify-between items-center inline-flex', className)} {...props} />
);
const HeaderLeft = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('py-2 rounded-lg justify-center items-center gap-3 flex', className)} {...props} />
);

const IconContainer = ({ isActive, type, className, ...props }: any) => (
  <div
    className={cn(
      'flex justify-center items-center',
      'w-4 h-4 rounded-[384px]',
      isActive ? 'border-2 border-purple-500' : 'border-2 border-neutral-90',
      className
    )}
    {...props}
  />
);

const InnerIcon = ({ isActive, className, ...props }: any) => (
  <div className={cn('w-2 h-2 rounded-full', isActive ? 'bg-purple-500' : 'bg-transparent', className)} {...props} />
);

const HeaderText = ({ type, className, ...props }: any) => (
  <div
    className={cn(
      'text-base font-semibold leading-none',
      type === 'trial' ? 'text-purple-700 font-bold' : 'text-neutral-700 font-medium',
      className
    )}
    {...props}
  />
);
const HeaderRight = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('justify-end items-center gap-0.5 flex', className)} {...props} />
);
const Price = ({ type, className, ...props }: any) => (
  <div
    className={cn(
      'font-semibold',
      type === 'trial' ? 'text-purple-700 text-2xl font-extrabold' : 'text-neutral-700 text-sm',
      className
    )}
    {...props}
  />
);
const Tax = ({ type, className, ...props }: any) => (
  <div
    className={cn(
      'text-sm font-light leading-[21px]',
      type === 'trial' ? 'text-neutral-700' : 'text-neutral-700',
      className
    )}
    {...props}
  />
);
const Description = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch text-[#666676] text-sm font-normal leading-[16px]', className)} {...props} />
);
const CardBody = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex-col justify-start items-start gap-1 flex', className)} {...props} />
);
const BodyText = ({ type, className, ...props }: any) => (
  <div
    className={cn(
      'self-stretch text-sm font-normal leading-[21px]',
      type === 'trial' ? 'text-neutral-700' : 'text-[#666676]',
      className
    )}
    {...props}
  />
);

const Footer = ({ isActive, className, ...props }: any) => (
  <div
    className={cn(
      'self-stretch h-12 px-20 py-2 rounded-lg justify-center items-center gap-5 inline-flex',
      isActive ? 'bg-transparent' : 'bg-transparent',
      className
    )}
    {...props}
  />
);

const FooterButton = (props: React.ComponentProps<typeof IconButton>) => (
  <IconButton {...props} className={cn('w-10 p-2 rounded-lg justify-center items-center flex', props.className)} />
);

const FooterText = ({ isActive, className, ...props }: any) => (
  <div
    className={cn(
      'w-auto whitespace-nowrap',
      'text-center',
      'text-base font-medium',
      isActive ? 'text-purple-700' : 'text-[#757583]',
      className
    )}
    {...props}
  />
);

const HeaderLeftComponent = ({
  isActive,
  headerTitle,
  type,
}: Pick<SlotPurchaseCardProps, 'isActive' | 'headerTitle' | 'type'>) => (
  <HeaderLeft>
    {type !== 'subscription' && (
      <IconContainer isActive={isActive} type={type}>
        <InnerIcon isActive={isActive} />
      </IconContainer>
    )}
    <HeaderText type={type}>{headerTitle}</HeaderText>
  </HeaderLeft>
);

const HeaderRightComponent = ({
  bundlePrice,
  slotPrice,
  type,
  discountPrice,
  loading,
  discount,
}: Pick<SlotPurchaseCardProps, 'bundlePrice' | 'slotPrice' | 'type' | 'discountPrice' | 'loading' | 'discount'>) => (
  <HeaderRight>
    {loading ? (
      <SkeletonTextLoading width="xSmall" />
    ) : (
      <>
        {discountPrice !== (slotPrice ?? 0) - (discount?.discount ?? 0) && (
          <h5 className="text-neutral-200 text-sm font-semibold line-through">{discountPrice}€</h5>
        )}
        <Price type={type}>
          {type !== 'custom'
            ? `${(bundlePrice || 0) - (discount?.discount ?? 0)}€${type === 'subscription' ? `/mese` : ''}`
            : `${(slotPrice || 0) - (discount?.discount ?? 0)}€`}
        </Price>
        <Tax type={type}>+ iva</Tax>
      </>
    )}
  </HeaderRight>
);

const CardBodyComponent = ({
  slotPurchaseDescriptions,
  type,
}: Pick<SlotPurchaseCardProps, 'slotPurchaseDescriptions' | 'type'>) => {
  return (
    <CardBody>
      {slotPurchaseDescriptions?.map((text, index) => (
        <BodyText key={index} type={type}>
          {text}
        </BodyText>
      ))}
    </CardBody>
  );
};

const FooterComponent = ({
  isActive,
  onSlotAdd,
  onSlotMinus,
  slotNumber,
}: Pick<SlotPurchaseCardProps, 'isActive' | 'onSlotAdd' | 'onSlotMinus' | 'slotNumber'>) => (
  <Footer isActive={isActive}>
    <FooterButton
      size="md"
      ariaLabel="minus"
      Icon={MinusIcon}
      variant={isActive ? 'restworld' : 'neutral'}
      disabled={!isActive || slotNumber === 1}
      onClick={onSlotMinus}
    />
    <FooterText isActive={isActive}>
      {slotNumber}
      {'\u00A0'}slot
    </FooterText>
    <FooterButton
      size="md"
      ariaLabel="plus"
      Icon={PlusIcon}
      variant={isActive ? 'restworld' : 'neutral'}
      disabled={!isActive || slotNumber === 10}
      onClick={onSlotAdd}
    />
  </Footer>
);

const LogoWatermark = () => (
  <svg
    className="absolute opacity-[0.03] pointer-events-none text-purple-500"
    style={{
      width: '550.063px',
      height: '467.895px',
      transform: 'rotate(-15deg)',
      left: '-183.699px',
      top: '-200.586px',
    }}
    width="320"
    height="200"
    viewBox="0 0 320 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M375.329 -67.3658C361.708 -76.0023 343.602 -71.9593 334.943 -58.3331C333.519 -56.0917 299.838 -2.50417 279.279 71.5403C268.479 110.478 262.014 147.654 258.176 176.611C244.025 166.168 228.871 155.466 212.892 144.609C198.699 134.986 183.52 124.986 167.514 115.895C190.887 72.3523 200.222 17.2607 191.333 -28.0868C183.053 -70.3601 165.943 -99.0332 139.054 -115.682C110.229 -133.528 74.8636 -133.831 48.9635 -116.417C8.15039 -89.0064 -11.7445 -18.7827 0.563755 54.3376C2.07319 63.3373 4.15644 73.0631 6.75614 83.388C-55.1053 93.1077 -108.471 134.355 -115.162 177.806C-119.33 204.802 -105.64 228.795 -78.568 241.966C-46.0751 257.778 0.0162276 251.286 51.1786 223.675C52.8604 222.75 54.5596 221.818 56.2591 220.864C73.1221 259.364 87.2842 287.254 88.9905 290.604C96.3094 304.951 113.96 310.672 128.358 303.363C135.328 299.801 140.493 293.753 142.91 286.321C145.327 278.89 144.673 270.949 141.115 263.995C140.395 262.548 123.815 229.8 105.578 187.242C115.194 179.425 124.089 171.381 132.127 163.178C148.489 172.007 164.727 182.674 180.031 193.052C233.684 229.454 262.753 254.424 263.058 254.674L308.834 294.208L311.423 233.76C311.45 233.064 314.756 162.591 335.692 87.2041C353.649 22.4717 383.172 -25.0537 384.37 -26.9906C393.018 -40.613 388.948 -58.729 375.329 -67.3658ZM-51.773 192.172C-56.5826 189.32 -58.5625 183.35 -57.9719 179.17C-56.8331 171.249 -39.8335 155.358 2.43748 144.031C7.19352 142.757 12.0653 141.761 16.9422 141.025C19.3462 140.665 21.7353 140.358 24.1197 140.099C27.1529 148.667 30.3757 157.661 33.8907 166.765C-8.4497 191.78 -40.831 198.621 -51.773 192.172ZM114.033 91.5894C98.3399 86.4135 82.758 83.0919 66.8088 81.4839C62.9776 67.8953 60.1135 55.5213 58.2818 44.6047C49.027 -10.2873 61.6241 -53.5385 78.1936 -66.3314C78.3475 -66.4672 96.5616 -79.0835 113.318 -68.6968C130.953 -57.7792 134.798 -36.6019 136.197 -19.3864C138.641 11.0666 133.91 43.5834 123.756 70.5193C120.927 78.0333 117.673 85.1073 114.033 91.5894Z"
      fill="currentColor"
    />
  </svg>
);

export function SlotPurchaseCard({
  isMostRequested,
  isActive,
  slotNumber,
  headerTitle,
  type,
  onClick,
  description,
  ctaLabel,
  onCtaClick,
  ctaStartIcon,
  isCtaDisabled,
  isCtaLoading,
  ...rest
}: SlotPurchaseCardProps) {
  const { t } = useTranslation('product-description-page');
  const { discount } = rest;

  return (
    <CardContainer onClick={onClick} isActive={isActive} type={type}>
      {type === 'trial' && <LogoWatermark />}
      {(discount?.discount ?? 0) > 0 && (
        <div className="self-stretch inline-flex flex-row justify-end">
          <Badge variant="tertiary">{`Sconto di benvenuto: -${discount?.discount ?? 0}€`}</Badge>
        </div>
      )}
      <ContentContainer>
        <div className="self-stretch flex-col justify-start items-start gap-1 flex">
          <HeaderTop>
            <HeaderLeftComponent type={type} isActive={isActive} headerTitle={headerTitle} />
            <HeaderRightComponent type={type} {...rest} />
          </HeaderTop>
          {description && <Description>{description}</Description>}
        </div>
        <CardBodyComponent {...rest} type={type} />
        {type === 'trial' && <Badge variant="fresh">offerta speciale</Badge>}
        {isMostRequested && (
          <Badge variant="success">
            <h5 className="text-green-700 text-sm font-medium">{t('most_requested')}</h5>
          </Badge>
        )}
      </ContentContainer>
      {(type === 'custom' || type === 'subscription') && (
        <FooterComponent slotNumber={slotNumber} isActive={isActive} {...rest} />
      )}
      {ctaLabel && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onCtaClick?.();
          }}
          fullwidth
          StartIcon={ctaStartIcon}
          variant={isActive && type === 'trial' ? 'primary' : isActive ? 'secondary-restworld' : 'secondary-neutral'}
          disabled={isCtaDisabled || !isActive}
          loading={isCtaLoading}
        >
          {ctaLabel}
        </Button>
      )}
    </CardContainer>
  );
}

export default SlotPurchaseCard;
